import type { Id, UserId } from '@/types/api/common'

/**
 * PLACEHOLDER endpoint map. The real API contract is not available yet.
 *
 * Every path in the app is built from this file, so replacing it with the real
 * contract is a single-file change and every mismatch shows up as a type error
 * at the gateway module that uses it.
 */
export const endpoints = {
  auth: {
    login: '/auth/login',
    /** Second factor of login (TURON-195): `{ challengeId, otp }` → the session. */
    loginVerifyOtp: '/auth/login/verify-otp',
    /** `{ challengeId }` → a fresh challenge, emailed again. */
    loginResendOtp: '/auth/login/resend-otp',
    logout: '/auth/logout',
    /**
     * Rotates the token pair and returns the user with it. There is no
     * `GET /auth/me`, so this is also the session-restore call.
     */
    refresh: '/auth/refresh',
    /**
     * SUPERSEDED (2026-09-18). A signed-in password change is
     * `endpoints.users.profilePassword` — the backend put it in its users
     * module, beside the profile it belongs to. Kept only so nothing that
     * imported this name breaks; it was never implemented under `/auth`.
     */
    changePassword: '/auth/change-password',
    /** Live (2026-09-02). Sends the reset email; always answers 202. */
    forgotPassword: '/auth/forgot-password',
    /** Live (2026-09-02). `{ token, password, confirmPassword }`. */
    resetPassword: '/auth/reset-password',
    /**
     * Live. `{ token }` — pre-flight check so the reset screen can say a link is
     * dead before the form is filled in, rather than at submit.
     */
    validateResetToken: '/auth/reset-password/validate',
  },

  dashboard: {
    /**
     * TURON-213. Option dates from today to six months ahead, grouped event →
     * option date → hotels and already sorted. No params, no pagination: the
     * window is the server's rule, never recomputed here.
     */
    upcomingEvents: '/dashboard/upcoming-events',
  },

  /**
   * LIVE contract, verified against the API (2026-09-01). The form saves section
   * by section, but every section is a partial `PATCH /customers/:id` — there
   * are no per-section paths.
   *
   * Copy is **server-side** via `action` — see the note there.
   */
  customers: {
    list: '/customers',
    create: '/customers',
    detail: (id: Id) => `/customers/${id}`,
    update: (id: Id) => `/customers/${id}`,
    remove: (id: Id) => `/customers/${id}`,
    /**
     * The linked-action endpoint. One path for every row action that starts a
     * workflow from a customer, discriminated by `customerAction` in the body
     * (`copy_customer` today; `add_reservation` / `add_waitlist` once those
     * modules exist).
     */
    action: '/customers/action',
    /** Top level, not nested under /customers. */
    types: '/customer-types',
    /*
     * No per-customer audit path exists. History comes from the shared audit
     * trail, filtered by resource — see `audit.trails`.
     */
  },

  /**
   * Read-only geographic reference data, id-based. `states` requires
   * `countryId`; `cities` requires `countryId` or `stateId`. The API rejects a
   * city that does not belong to the selected state/country.
   */
  /** API-owned enums (event statuses, regions, …) — see `types/api/meta.ts`. */
  meta: '/meta',

  locations: {
    regions: '/locations/regions',
    countries: '/locations/countries',
    states: '/locations/states',
    cities: '/locations/cities',
  },

  events: {
    list: '/events',
    create: '/events',
    detail: (id: Id) => `/events/${id}`,
    update: (id: Id) => `/events/${id}`,
    remove: (id: Id) => `/events/${id}`,
    /** Row actions (`duplicate_event`), discriminated by `eventAction` in the body. */
    action: '/events/action',
    status: (id: Id) => `/events/${id}/status`,
    fees: (id: Id) => `/events/${id}/fees`,
    contacts: (id: Id) => `/events/${id}/contacts`,
    media: (id: Id) => `/events/${id}/media`,
    /** Large Event configuration — ACL gated. */
    areas: (id: Id) => `/events/${id}/areas`,
    groups: (id: Id) => `/events/${id}/hotel-groups`,
    /**
     * The hotels on an event (GET), and "Add hotel to this event" (POST
     * `{ hotelId }`, which records a bare group). Served by the backend's
     * `hotels` module: the link is a hotel group.
     */
    hotels: (id: Id) => `/events/${id}/hotels`,
    audit: (id: Id) => `/events/${id}/audit`,
  },

  /**
   * The active amenity catalogue the hotel form picks from (`hotel.view`).
   * Not `settings.amenities`, which is the Global Settings CRUD surface.
   */
  amenities: {
    list: '/amenities',
  },

  /**
   * The currency lookup the hotel form's Currency select offers
   * (`?activeOnly=true`). LIVE (2026-10-08). Not `settings.currencies`, which
   * is the Global Settings CRUD surface.
   */
  currencies: {
    list: '/currencies',
  },

  hotels: {
    list: '/hotels',
    create: '/hotels',
    detail: (id: Id) => `/hotels/${id}`,
    update: (id: Id) => `/hotels/${id}`,
    remove: (id: Id) => `/hotels/${id}`,
    customInfo: (id: Id) => `/hotels/${id}/custom-information`,
    contacts: (id: Id) => `/hotels/${id}/contacts`,
    images: (id: Id) => `/hotels/${id}/images`,
    amenities: (id: Id) => `/hotels/${id}/amenities`,
    roomTypes: (id: Id) => `/hotels/${id}/room-types`,
    roomType: (hotelId: Id, roomTypeId: Id) => `/hotels/${hotelId}/room-types/${roomTypeId}`,
    /**
     * Event-Hotel Groups are a **sub-resource of the hotel** (verified
     * 2026-09-28): list + create on the collection, read / update / delete on
     * the pair. There is no top-level `/event-hotel-groups` route.
     */
    groups: (hotelId: Id) => `/hotels/${hotelId}/groups`,
    group: (hotelId: Id, groupId: Id) => `/hotels/${hotelId}/groups/${groupId}`,
    /**
     * One timeline across the hotel, its rate, groups and room types —
     * server-paged, `audit-trail.view` (backend TURON-145, verified 2026-10-01).
     */
    logs: (hotelId: Id) => `/hotels/${hotelId}/logs`,
  },

  rooms: {
    /**
     * The standalone Room Inventory listing the 2026-09-24 design introduces:
     * every room block across events and hotels, not one group's. The
     * group-scoped routes below stay — they are how a group screen will read
     * its own inventory.
     */
    /**
     * The Rooms listing (2026-10-27): **one row per room type × event**, with
     * each event's group allotments and lowest rate.
     */
    list: '/room-types',
    /** One room type's inventory: every event × group block with its nights. */
    roomTypeInventory: (roomTypeId: Id) => `/room-inventory/room-types/${roomTypeId}`,
    /**
     * One room type's change history, paged — the same audit entries as
     * `hotels.logs`. Keyed by the ROOM TYPE id (user, 2026-10-29), not a night;
     * moved under `/room-types/` (user, 2026-11-02).
     */
    roomTypeLogs: (roomTypeId: Id) => `/room-inventory/room-types/${roomTypeId}/logs`,
    /**
     * `/room-inventory` — **one row per hotel × room type × night** (verified
     * 2026-09-28). Read, update and delete address one night by its id; a
     * delete is a PATCH of `isActive: false` on `update` (2026-10-06).
     */
    create: '/room-inventory',
    detail: (id: Id) => `/room-inventory/${id}`,
    update: (id: Id) => `/room-inventory/${id}`,
    /** A hotel's room types: list + create (the Room Types multi-select reads it too). */
    roomTypes: (hotelId: Id) => `/hotels/${hotelId}/room-types`,
    /** One room type: read, update and (soft) delete — keyed on both ids. */
    roomType: (hotelId: Id, roomTypeId: Id) => `/hotels/${hotelId}/room-types/${roomTypeId}`,
    /** Per-date inventory + rates for a group. */
    inventory: (groupId: Id) => `/event-hotel-groups/${groupId}/rooms`,
    upsertRange: (groupId: Id) => `/event-hotel-groups/${groupId}/rooms/range`,
    upsertDate: (groupId: Id) => `/event-hotel-groups/${groupId}/rooms/date`,
    copyRates: (groupId: Id) => `/event-hotel-groups/${groupId}/rooms/copy-rates`,
    removeDate: (groupId: Id, date: string) => `/event-hotel-groups/${groupId}/rooms/${date}`,
  },

  /**
   * Reservations — LIVE (backend `reservations` module, integration spec
   * 2026-10-26; `src/screens/reservations/agent.md` §7). The detail read
   * carries guests, notes, fees, the payment box and the commission, so writes
   * either answer the detail or a single line.
   */
  reservations: {
    list: '/reservations',
    create: '/reservations',
    /** GET the detail; PATCH any subset (notes, status, the stay). */
    detail: (id: Id) => `/reservations/${id}`,
    customerLookup: '/reservations/lookups/customers',
    eventLookup: '/reservations/lookups/events',
    /** The hotel → group → room type tree for one event. */
    eventOptions: (eventId: Id) => `/reservations/lookups/events/${eventId}/options`,
    availability: '/reservations/availability',
    confirm: (id: Id) => `/reservations/${id}/confirm`,
    cancel: (id: Id) => `/reservations/${id}/cancel`,
    sendCancellation: (id: Id) => `/reservations/${id}/send-cancellation`,
    sendConfirmation: (id: Id) => `/reservations/${id}/send-confirmation`,
    guests: (id: Id) => `/reservations/${id}/guests`,
    guest: (id: Id, guestId: Id) => `/reservations/${id}/guests/${guestId}`,
    fees: (id: Id) => `/reservations/${id}/fees`,
    fee: (id: Id, feeId: Id) => `/reservations/${id}/fees/${feeId}`,
    feeReceipt: (id: Id, feeId: Id) => `/reservations/${id}/fees/${feeId}/send-receipt`,
    /** The public VGS Collect.js settings — no secret. */
    vaultConfig: '/payments/vault-config',
    paymentMethods: (id: Id) => `/reservations/${id}/payment-methods`,
    /** Authorize & Capture the fee balance. `Idempotency-Key` required. */
    charge: (id: Id) => `/reservations/${id}/charge`,
    /** Capture the website's hold. `Idempotency-Key` required. */
    capture: (id: Id) => `/reservations/${id}/capture`,
    resolveTransaction: (id: Id, transactionId: Id) =>
      `/reservations/${id}/transactions/${transactionId}/resolve`,
    /** Pay One (PUT). */
    commission: (id: Id) => `/reservations/${id}/commission`,
    commissionPayAll: (id: Id) => `/reservations/${id}/commission/pay-all`,
    pullExchangeRate: (id: Id) => `/reservations/${id}/commission/pull-exchange-rate`,
  },

  /**
   * The wait list (backend `wait-list` module, TURON-135) — five endpoints.
   * There is no delete (`cancelled` withdraws an entry). The detail carries the
   * change log and the email log. See `src/screens/waitlist/agent.md` §7.
   */
  waitlist: {
    list: '/wait-list',
    create: '/wait-list',
    /** The entry, plus `logs[]` and `emailLogs[]`; `meta.statuses`. */
    detail: (id: Id) => `/wait-list/${id}`,
    /** Any subset of the fields; absent leaves, null clears. */
    update: (id: Id) => `/wait-list/${id}`,
    /** No body — goes to the customer's primary address. */
    sendConfirmation: (id: Id) => `/wait-list/${id}/send-confirmation`,
    /**
     * The hotels on one event (`?eventId=`), by name — live, active hotels
     * only. `[]` = no contracted hotels yet; 404 = unknown event.
     */
    lookupHotels: '/wait-list/lookups/hotels',
  },

  /** PCI-sensitive. Masked payment profiles only — never raw card data. */
  transactions: {
    list: '/transactions',
    detail: (id: Id) => `/transactions/${id}`,
    byReservation: (reservationId: Id) => `/reservations/${reservationId}/transactions`,
    refund: (id: Id) => `/transactions/${id}/refund`,
    void: (id: Id) => `/transactions/${id}/void`,
    /** Tokenised profiles from the vault — masked digits only. */
    paymentProfiles: (customerId: Id) => `/customers/${customerId}/payment-profiles`,
  },

  commission: {
    list: '/commission',
    detail: (id: Id) => `/commission/${id}`,
    payOne: (id: Id) => `/commission/${id}/pay`,
    payAll: '/commission/pay-all',
  },

  /**
   * Secure Hotel Portal (`secure-link`), staff side — the backend's
   * `secure-links` module (its `agent.md` §9). No response ever carries a card
   * or the full link token: the link is returned masked, the token exists only
   * in the email.
   */
  secureLinks: {
    list: '/secure-links',
    /** The listing's four stat cards. */
    summary: '/secure-links/summary',
    /** `?eventId=&hotelId=` — only the reservations the system marks Ready to Send. */
    eligibleReservations: '/secure-links/eligible-reservations',
    /** `?hotelId=` — the hotel's approved primary recipient. */
    recipient: '/secure-links/recipient',
    /** GET (`secure-link.view`) / PUT (`setting.edit`) — reveal limit + lock minutes. */
    settings: '/secure-links/settings',
    /** Creates the batch and sends the email in one request. `Idempotency-Key` required. */
    create: '/secure-links',
    detail: (id: Id) => `/secure-links/${id}`,
    reservations: (id: Id) => `/secure-links/${id}/reservations`,
    sends: (id: Id) => `/secure-links/${id}/sends`,
    activity: (id: Id) => `/secure-links/${id}/activity`,
    /** Issues a new link + expiry; the previous link stops working. `Idempotency-Key` required. */
    resend: (id: Id) => `/secure-links/${id}/resend`,
    revoke: (id: Id) => `/secure-links/${id}/revoke`,
  },

  /**
   * The hotel-facing Secure Hotel Portal (backend `secure-links` agent.md
   * §9). Served OUTSIDE the staff tree — `/api/v1/hotel-portal`, not
   * `/api/v1/admin` — so every call is made with `publicApi`. The `access`
   * routes take the emailed link token and are called without any session;
   * every other route carries the portal session (`portalAuth`), never a
   * staff token.
   */
  hotelPortal: {
    access: (token: string) => `/hotel-portal/access/${encodeURIComponent(token)}`,
    sendCode: (token: string) => `/hotel-portal/access/${encodeURIComponent(token)}/code`,
    verifyCode: (token: string) => `/hotel-portal/access/${encodeURIComponent(token)}/verify`,
    me: '/hotel-portal/me',
    logout: '/hotel-portal/logout',
    events: '/hotel-portal/events',
    eventSummary: (eventId: Id) => `/hotel-portal/events/${eventId}/summary`,
    /** `?status=&search=` */
    eventReservations: (eventId: Id) => `/hotel-portal/events/${eventId}/reservations`,
    /** Logs one reveal and returns the card's vault alias — never its digits. */
    reveal: (reservationId: Id) => `/hotel-portal/reservations/${reservationId}/reveal`,
    confirm: (reservationId: Id) => `/hotel-portal/reservations/${reservationId}/confirm`,
  },

  /**
   * PLACEHOLDER — no report routes are published yet. Revenue SUMs is served by
   * `gateway/modules/reports.static.ts` (screens/reports/agent.md §7).
   */
  reports: {
    confirmationList: '/reports/confirmation-list',
    emailBlast: '/reports/email-blast',
    revenue: '/reports/revenue',
    /** Every report filter's options. */
    filters: '/reports/filters',
    /** The hotels connected to one event — Room Count's Hotel filter. */
    eventHotels: (eventId: Id) => `/reports/events/${eventId}/hotels`,
    optionDates: '/reports/option-dates',
    revenueDetail: '/reports/revenue-detail',
    emailBlastSend: '/reports/email-blast/send',
    roomCount: '/reports/room-count',
    topCustomers: '/reports/top-customers',
    yearlySales: '/reports/comparison/yearly-sales',
    staffComparison: '/reports/comparison/staff',
  },

  /**
   * Global Configuration. Each resource: list + `/:id` (PATCH).
   *
   * **LIVE** (2026-10-13): amenities, additional types (also POST), email
   * templates (also GET `/:id`) and system messages; currencies (also PATCH
   * `/:code/status`), regions and countries since 2026-10-16. **PLACEHOLDER**:
   * email activity, states and exchange rates (screens/settings/agent.md §7).
   */
  settings: {
    emailTemplates: '/settings/email-templates',
    emailTemplate: (id: Id) => `/settings/email-templates/${id}`,
    emailActivity: '/settings/email-activity',
    emailTypeToggle: (type: string) => `/settings/email-types/${type}`,
    amenities: '/settings/amenities',
    amenity: (id: Id) => `/settings/amenities/${id}`,
    additionalTypes: '/settings/additional-types',
    additionalType: (id: Id) => `/settings/additional-types/${id}`,
    regions: '/settings/regions',
    region: (id: Id) => `/settings/regions/${id}`,
    systemMessages: '/settings/system-messages',
    systemMessage: (id: Id) => `/settings/system-messages/${id}`,
    countries: '/settings/countries',
    country: (id: Id) => `/settings/countries/${id}`,
    /** States for a country. INVENTED PLACEHOLDER — see customers/agent.md §23.5. */
    states: '/settings/states',
    currencies: '/settings/currencies',
    currency: (id: Id) => `/settings/currencies/${id}`,
    /** Activate / deactivate alone — `{ isActive }`. */
    currencyStatus: (id: Id) => `/settings/currencies/${id}/status`,
    /** POST — every rate to USD from ExchangeRate-API now (turon-admin-be currencies §24). */
    currencyRatesRefresh: '/settings/currencies/rates/refresh',
    exchangeRates: '/settings/exchange-rates',
    /**
     * GET + PUT, one record. LIVE (turon-admin-be `modules/support`,
     * 2026-10-20) — screens/settings/agent.md §25.
     */
    officeHours: '/settings/office-hours',
  },

  /**
   * Website Content (CMS) — the website's fixed set of pages, addressed by page
   * key (`about-us`). LIVE (turon-admin-be `modules/cms`, 2026-10-16). Publish
   * also enables a page and unpublish disables it — see
   * `src/screens/cms/agent.md` §7.
   */
  cms: {
    pages: '/cms/pages',
    page: (pageKey: string) => `/cms/pages/${pageKey}`,
    pageDraft: (pageKey: string) => `/cms/pages/${pageKey}/draft`,
    pagePublish: (pageKey: string) => `/cms/pages/${pageKey}/publish`,
    pageUnpublish: (pageKey: string) => `/cms/pages/${pageKey}/unpublish`,
    media: '/cms/media',
  },

  /**
   * Users and roles are keyed **numerically** (`UserId`), unlike the UUID records
   * above.
   *
   * The `/users/*` paths are a **LIVE** contract, verified against the API
   * (2026-09-17). The backend's `users` module serves them under
   * `/api/v1/admin`; the static source that stood in for them is gone.
   *
   * `roles`, `role`, `rolePermissions`, `profile` and `preferences` are NOT part
   * of that module. The first three belong to the legacy `/users/roles` screens
   * (the Roles module supersedes them — see `roles` below); the last two are
   * placeholders for screens that do not exist yet.
   */
  users: {
    list: '/users',
    create: '/users',
    detail: (id: UserId) => `/users/${id}`,
    update: (id: UserId) => `/users/${id}`,
    remove: (id: UserId) => `/users/${id}`,
    /** `{ isActive }`. Activate / deactivate without touching anything else. */
    status: (id: UserId) => `/users/${id}/status`,
    roles: '/roles',
    role: (id: UserId) => `/roles/${id}`,
    rolePermissions: (id: UserId) => `/roles/${id}/permissions`,
    /**
     * The signed-in user's own account. **LIVE (2026-09-18)** — served by the
     * backend's `users` module behind `authenticate` and no permission, so every
     * signed-in user can reach their own profile whatever their role grants.
     */
    profile: '/users/profile',
    /**
     * Active agents (`users.agent = 'Yes'`) for the group form's Agent select —
     * `[{ id, firstName, lastName, userInitials }]`, unpaged. Gated by
     * `user.view` **or** `hotel.edit`.
     */
    agents: '/users/agents',
    /** `{ currentPassword, newPassword, confirmPassword? }`. LIVE (2026-09-18). */
    profilePassword: '/users/profile/password',
    /** NOT IMPLEMENTED on the backend yet. Placeholder path (User Settings). */
    preferences: '/users/preferences',
  },

  /**
   * Roles & Permissions (User Management > Roles). Keyed **numerically**
   * (`UserId`), like users.
   *
   * LIVE contract, verified against the API (2026-09-16). The backend's `acl`
   * module serves all of these under `/api/v1/admin`; the static source that
   * stood in for them is gone.
   *
   * `remove` exists and is **not called by any screen**: the design's row menu
   * offers Edit Role only, so no delete control was invented
   * (`src/screens/roles/agent.md` §4). It is here because the contract is, and
   * because a UI for it is a design decision rather than a missing endpoint.
   *
   * Separate from `users.roles` on purpose: that block belongs to the legacy
   * `/users/roles` screens, which this module does not touch.
   */
  roles: {
    list: '/roles',
    create: '/roles',
    detail: (id: UserId) => `/roles/${id}`,
    update: (id: UserId) => `/roles/${id}`,
    remove: (id: UserId) => `/roles/${id}`,
    /**
     * The resource x action catalogue the permission matrix renders.
     *
     * `/permissions/catalog`, not `/permissions` — the latter is the flat
     * permission table, which is a different shape and a different question.
     */
    catalog: '/permissions/catalog',
    /**
     * Every grantable key as a flat list — `{ id, name, resource, action,
     * description }` per row, with `?search=` and `?resource=` filters.
     *
     * Not called by a screen: the matrix needs the grouped `catalog` shape, not
     * this one. It is mapped here because it is part of the module's published
     * contract, the same way the not-yet-built modules' paths are.
     */
    permissions: '/permissions',
  },

  partners: {
    list: '/partners',
    create: '/partners',
    detail: (id: Id) => `/partners/${id}`,
    update: (id: Id) => `/partners/${id}`,
    publish: (id: Id) => `/partners/${id}/publish`,
  },

  /**
   * Audit trail. VERIFIED live 2026-09-08.
   *
   * `trails` is the listing, filtered by `resourceType` + `resourceId` (the API
   * rejects `resourceId` without `resourceType`). This is the ONLY call the
   * Customer Logs modal makes, by client instruction.
   *
   * `trail(id)` is the per-entry detail — the only place `oldValues`/`newValues`
   * appear. Nothing calls it today; it exists for the Audit module, which will
   * need a single entry's before/after.
   */
  audit: {
    trails: '/audit-trails',
    trail: (id: Id) => `/audit-trails/${id}`,
    apiLogs: '/audit/api-logs',
    apiLog: (id: Id) => `/audit/api-logs/${id}`,
  },
} as const
